// Incidents (Sentry) and pull request links (GitHub) as the API exposes them: global (every
// project the caller can read, RLS-scoped) or one project. Project-less incidents are only
// listed for organization maintainers (`unassigned=true` is role-gated by the API).
import { useState } from "react";
import { Link } from "react-router";
import { useI18n } from "../../i18n/index.tsx";
import type { MessageKey } from "../../i18n/en.ts";
import { listIncidents, listPullRequestLinks } from "../../api/endpoints-wave3.ts";
import { INCIDENT_STATES, type Incident, type IncidentState, type PullRequestLink } from "../../api/types-wave3.ts";
import { Button } from "../../components/Button.tsx";
import { SelectField } from "../../components/Field.tsx";
import { EmptyState, ErrorBanner, LoadingBlock } from "../../components/Feedback.tsx";
import { AlertTriangleIcon, ExternalLinkIcon, GitBranchIcon, RefreshIcon } from "../../components/icons.tsx";
import { useOrgRole } from "../config/shared.tsx";
import { Hash, LoadMore, ShortId, StatePill, useCursorList, useProjectOptions } from "../wave3/common.tsx";

const PAGE = 50;

const INCIDENT_VIEW: Record<IncidentState, { tone: "success" | "danger" | "warning" | "info" | "neutral"; label: MessageKey }> = {
  open: { tone: "danger", label: "incidentState.open" },
  linked: { tone: "info", label: "incidentState.linked" },
  resolved: { tone: "success", label: "incidentState.resolved" },
  ignored: { tone: "neutral", label: "incidentState.ignored" },
  regressed: { tone: "warning", label: "incidentState.regressed" },
};

const PR_VIEW: Record<PullRequestLink["state"], { tone: "success" | "info" | "neutral"; label: MessageKey }> = {
  open: { tone: "info", label: "prState.open" },
  merged: { tone: "success", label: "prState.merged" },
  closed: { tone: "neutral", label: "prState.closed" },
};

export function IncidentsList({ projectId }: { projectId: string | null }) {
  const { t, formatDateTime, formatRelative } = useI18n();
  const { atLeast } = useOrgRole();
  const projects = useProjectOptions();
  const [state, setState] = useState<IncidentState | "">("");
  const [scope, setScope] = useState("");
  const effectiveProject = projectId ?? (scope && scope !== "unassigned" ? scope : null);
  const list = useCursorList<Incident>(
    (cursor, signal) =>
      listIncidents({ ...(effectiveProject ? { project_id: effectiveProject } : {}), ...(scope === "unassigned" && !projectId ? { unassigned: true } : {}), state, cursor, limit: PAGE }, signal),
    [effectiveProject, scope, state],
  );
  const titleId = `incidents-${projectId ?? "all"}-title`;
  return (
    <section className="card" aria-labelledby={titleId} data-testid="incidents-list">
      <div className="card-header">
        <AlertTriangleIcon size={18} />
        <h2 id={titleId} className="card-title" style={{ flex: 1 }}>
          {t("incidents.title")}
        </h2>
        <Button size="sm" variant="ghost" icon={<RefreshIcon size={14} />} onClick={list.reload}>
          {t("common.refresh")}
        </Button>
      </div>
      <div className="card-body stack">
        <div className="w3-toolbar">
          <SelectField label={t("incidents.filter.state")} value={state} onChange={(e) => setState(e.target.value as IncidentState | "")}>
            <option value="">{t("common.all")}</option>
            {INCIDENT_STATES.map((s) => (
              <option key={s} value={s}>
                {t(INCIDENT_VIEW[s].label)}
              </option>
            ))}
          </SelectField>
          {projectId ? null : (
            <SelectField label={t("incidents.filter.project")} value={scope} onChange={(e) => setScope(e.target.value)}>
              <option value="">{t("inbox.filter.anyProject")}</option>
              {atLeast("maintainer") ? <option value="unassigned">{t("incidents.unassigned")}</option> : null}
              {projects.options.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </SelectField>
          )}
        </div>
        {list.error && !list.items ? (
          <ErrorBanner error={list.error} onRetry={list.reload} title={t("incidents.loadFailed")} />
        ) : list.items === null ? (
          <LoadingBlock label={t("common.loading")} />
        ) : list.items.length === 0 ? (
          <EmptyState icon={<AlertTriangleIcon />} title={t("incidents.emptyTitle")} headingLevel={3}>
            <p>{t("incidents.emptyBody")}</p>
          </EmptyState>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <caption className="sr-only">{t("incidents.title")}</caption>
              <thead>
                <tr>
                  <th scope="col">{t("incidents.col.title")}</th>
                  <th scope="col">{t("incidents.col.state")}</th>
                  {projectId ? null : <th scope="col">{t("inbox.col.project")}</th>}
                  <th scope="col">{t("incidents.col.environment")}</th>
                  <th scope="col">{t("incidents.col.events")}</th>
                  <th scope="col">{t("incidents.col.lastSeen")}</th>
                  <th scope="col">{t("incidents.col.task")}</th>
                </tr>
              </thead>
              <tbody>
                {list.items.map((i) => {
                  const view = INCIDENT_VIEW[i.state];
                  return (
                    <tr key={i.id} data-testid="incident-row" data-state={i.state}>
                      <td>
                        <span className="user-content">{i.title}</span>
                        <span className="subtle" style={{ display: "block" }}>
                          {i.level ? <span className="badge">{i.level}</span> : null} <Hash value={i.fingerprint} />
                        </span>
                      </td>
                      <td>
                        <StatePill tone={view.tone} label={t(view.label)} />
                      </td>
                      {projectId ? null : (
                        <td>
                          {i.project_id ? (
                            <Link to={`/projects/${i.project_id}/signals`} className="link-subtle">
                              {projects.nameOf(i.project_id) ?? <ShortId id={i.project_id} />}
                            </Link>
                          ) : (
                            <span className="subtle">{t("inbox.unassigned")}</span>
                          )}
                        </td>
                      )}
                      <td>
                        {i.environment ?? "—"}
                        {i.release_ref ? (
                          <span className="subtle mono" style={{ display: "block" }}>
                            {i.release_ref}
                          </span>
                        ) : null}
                      </td>
                      <td>{i.event_count}</td>
                      <td>
                        <span title={formatDateTime(i.last_seen_at)}>{formatRelative(i.last_seen_at)}</span>
                        <span className="subtle" style={{ display: "block" }}>
                          {t("incidents.firstSeen", { when: formatDateTime(i.first_seen_at) })}
                        </span>
                      </td>
                      <td>
                        {i.task_id && i.project_id ? (
                          <Link to={`/projects/${i.project_id}/kanban?task=${i.task_id}`} className="link-subtle">
                            {t("incidents.openTask")}
                          </Link>
                        ) : (
                          <span className="subtle">{t("incidents.noTask")}</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {list.moreError ? <ErrorBanner error={list.moreError} onRetry={() => void list.loadMore()} /> : null}
        <LoadMore cursor={list.cursor} busy={list.moreBusy} count={list.items?.length ?? 0} onMore={() => void list.loadMore()} />
      </div>
    </section>
  );
}

export function PullRequestList({ projectId }: { projectId: string | null }) {
  const { t, formatDateTime, formatRelative } = useI18n();
  const projects = useProjectOptions();
  const [scope, setScope] = useState("");
  const effectiveProject = projectId ?? (scope || null);
  const list = useCursorList<PullRequestLink>((cursor, signal) => listPullRequestLinks({ ...(effectiveProject ? { project_id: effectiveProject } : {}), cursor, limit: PAGE }, signal), [effectiveProject]);
  const titleId = `prs-${projectId ?? "all"}-title`;
  return (
    <section className="card" aria-labelledby={titleId} data-testid="pr-list">
      <div className="card-header">
        <GitBranchIcon size={18} />
        <h2 id={titleId} className="card-title" style={{ flex: 1 }}>
          {t("prs.title")}
        </h2>
        <Button size="sm" variant="ghost" icon={<RefreshIcon size={14} />} onClick={list.reload}>
          {t("common.refresh")}
        </Button>
      </div>
      <div className="card-body stack">
        {projectId ? null : (
          <div className="w3-toolbar">
            <SelectField label={t("incidents.filter.project")} value={scope} onChange={(e) => setScope(e.target.value)}>
              <option value="">{t("inbox.filter.anyProject")}</option>
              {projects.options.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </SelectField>
          </div>
        )}
        {list.error && !list.items ? (
          <ErrorBanner error={list.error} onRetry={list.reload} title={t("prs.loadFailed")} />
        ) : list.items === null ? (
          <LoadingBlock label={t("common.loading")} />
        ) : list.items.length === 0 ? (
          <EmptyState icon={<GitBranchIcon />} title={t("prs.emptyTitle")} headingLevel={3}>
            <p>{t("prs.emptyBody")}</p>
          </EmptyState>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <caption className="sr-only">{t("prs.title")}</caption>
              <thead>
                <tr>
                  <th scope="col">{t("prs.col.pr")}</th>
                  <th scope="col">{t("prs.col.state")}</th>
                  {projectId ? null : <th scope="col">{t("inbox.col.project")}</th>}
                  <th scope="col">{t("prs.col.branch")}</th>
                  <th scope="col">{t("prs.col.head")}</th>
                  <th scope="col">{t("prs.col.task")}</th>
                  <th scope="col">{t("prs.col.updated")}</th>
                </tr>
              </thead>
              <tbody>
                {list.items.map((pr) => {
                  const view = PR_VIEW[pr.state];
                  const label = `${pr.repository}#${pr.pr_number}`;
                  return (
                    <tr key={pr.id} data-testid="pr-row" data-state={pr.state}>
                      <td>
                        {pr.url ? (
                          <a href={pr.url} target="_blank" rel="noopener noreferrer" className="link-subtle">
                            <span className="mono">{label}</span>
                            <ExternalLinkIcon size={14} />
                            <span className="sr-only">{t("common.opensNewTab")}</span>
                          </a>
                        ) : (
                          <span className="mono">{label}</span>
                        )}
                      </td>
                      <td>
                        <StatePill tone={view.tone} label={t(view.label)} />
                      </td>
                      {projectId ? null : (
                        <td>
                          <Link to={`/projects/${pr.project_id}/signals`} className="link-subtle">
                            {projects.nameOf(pr.project_id) ?? <ShortId id={pr.project_id} />}
                          </Link>
                        </td>
                      )}
                      <td className="mono">{pr.base_branch ?? "—"}</td>
                      <td>
                        <Hash value={pr.head_sha} />
                        <span className="subtle" style={{ display: "block" }}>
                          {t("prs.headSeq", { n: pr.head_seq })}
                        </span>
                      </td>
                      <td>
                        <Link to={`/projects/${pr.project_id}/kanban?task=${pr.task_id}`} className="link-subtle">
                          {t("incidents.openTask")}
                        </Link>
                      </td>
                      <td>
                        <span title={formatDateTime(pr.updated_at)}>{formatRelative(pr.updated_at)}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {list.moreError ? <ErrorBanner error={list.moreError} onRetry={() => void list.loadMore()} /> : null}
        <LoadMore cursor={list.cursor} busy={list.moreBusy} count={list.items?.length ?? 0} onMore={() => void list.loadMore()} />
      </div>
    </section>
  );
}
